"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { FiAlertTriangle, FiTrash2 } from "react-icons/fi";
import DeleteConfirmationModal from "@/components/delete-confirmation-modal";
import Button from "@/components/ui/button";
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import HeightInput from "@/components/ui/height-input";
import { Label } from "@/components/ui/label";
import { formatDate, formatTime } from "@/lib/format-data";
import { feetInchesToTotalInches, inchesToFeetInches, isHeightField, isNumericDataType, roundToQuarterInch } from "@/lib/measurement-utils";
import { toast } from "@/lib/use-toast";
import type { MeasurementField, MeasurementValueSummary, MeasurementValueUpdate } from "@/types/measurement";
import { MeasurementInput } from "./measurement-input";
import { useArchiveMeasurementValueMutation, useUpdateMeasurementValueMutation } from "@/query/patient/measurements";
interface EditMeasurementValueDialogProps {
  value: MeasurementValueSummary;
  field: MeasurementField;
  patientId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}
type EditFormValues = {
  value: string | string[];
  heightValue: {
    feet: string;
    inches: string;
  };
};
function getInitialFormValue(value: MeasurementValueSummary, field: MeasurementField): string | string[] {
  if (field.dataType === "STRING_ARRAY") {
    return value.arrayValue ?? [];
  }
  if (isNumericDataType(field.dataType)) {
    return value.numericValue?.toString() ?? "";
  }
  return value.textValue ?? "";
}
function getInitialHeightValue(value: MeasurementValueSummary, field: MeasurementField): {
  feet: string;
  inches: string;
} {
  if (!isHeightField(field) || value.numericValue == null) {
    return {
      feet: "",
      inches: ""
    };
  }
  const {
    feet,
    inches
  } = inchesToFeetInches(value.numericValue);
  return {
    feet: String(feet),
    inches: String(inches)
  };
}
function buildUpdatePayload(data: EditFormValues, field: MeasurementField): MeasurementValueUpdate {
  if (isHeightField(field)) {
    const feet = parseInt(data.heightValue.feet, 10) || 0;
    const inches = roundToQuarterInch(parseFloat(data.heightValue.inches) || 0);
    return {
      numericValue: feetInchesToTotalInches(feet, inches)
    };
  }
  if (field.dataType === "STRING_ARRAY") {
    return {
      arrayValue: Array.isArray(data.value) ? data.value : []
    };
  }
  const raw = typeof data.value === "string" ? data.value : "";
  if (isNumericDataType(field.dataType)) {
    return {
      numericValue: parseFloat(raw)
    };
  }
  return {
    textValue: raw
  };
}
export default function EditMeasurementValueDialog(t0) {
  const $ = _c(78);
  const {
    value,
    field,
    patientId,
    open,
    onOpenChange
  } = t0;
  let t1;
  if ($[0] !== field.dataType) {
    t1 = isNumericDataType(field.dataType);
    $[0] = field.dataType;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isNumeric = t1;
  const isArray = field.dataType === "STRING_ARRAY";
  let t2;
  if ($[2] !== field) {
    t2 = isHeightField(field);
    $[2] = field;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const isHeight = t2;
  const mutation = useUpdateMeasurementValueMutation(patientId);
  const archiveValueMutation = useArchiveMeasurementValueMutation(patientId);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  let t3;
  if ($[4] !== field || $[5] !== value) {
    t3 = getInitialFormValue(value, field);
    $[4] = field;
    $[5] = value;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== field || $[8] !== value) {
    t4 = getInitialHeightValue(value, field);
    $[7] = field;
    $[8] = value;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t3 || $[11] !== t4) {
    t5 = {
      value: t3,
      heightValue: t4
    };
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const defaultValues = t5;
  let t6;
  if ($[13] !== value.measurementDate) {
    bb0: {
      if (!value.measurementDate) {
        t6 = "the selected time";
        break bb0;
      }
      const date = new Date(value.measurementDate);
      if (Number.isNaN(date.getTime())) {
        t6 = "the selected time";
        break bb0;
      }
      t6 = `${formatDate(date)} at ${formatTime(date)}`;
    }
    $[13] = value.measurementDate;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const recordedAtText = t6;
  let t7;
  if ($[15] !== defaultValues) {
    t7 = {
      mode: "onSubmit",
      defaultValues
    };
    $[15] = defaultValues;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const {
    handleSubmit,
    control,
    formState: t8
  } = useForm(t7);
  const {
    errors
  } = t8;
  let t9;
  if ($[17] !== field || $[18] !== mutation || $[19] !== onOpenChange || $[20] !== value.id) {
    t9 = async data => {
      await mutation.mutateAsync({
        valueId: value.id,
        data: buildUpdatePayload(data, field)
      });
      toast({
        title: "Updated",
        description: "Measurement value updated successfully"
      });
      onOpenChange(false);
    };
    $[17] = field;
    $[18] = mutation;
    $[19] = onOpenChange;
    $[20] = value.id;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== handleSubmit || $[23] !== t9) {
    t10 = handleSubmit(t9);
    $[22] = handleSubmit;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const onSubmit = t10;
  let t11;
  if ($[25] !== archiveValueMutation || $[26] !== onOpenChange || $[27] !== value.id) {
    t11 = async () => {
      await archiveValueMutation.mutateAsync(value.id);
      toast({
        title: "Deleted",
        description: "Measurement value deleted successfully"
      });
      onOpenChange(false);
    };
    $[25] = archiveValueMutation;
    $[26] = onOpenChange;
    $[27] = value.id;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  const handleDelete = t11;
  const deleteConfirmationSubtitle = `Are you sure you want to delete this ${field.name} value recorded on ${recordedAtText}?`;
  let t12;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <DialogHeader><DialogTitle>Edit Value</DialogTitle></DialogHeader>;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <FiAlertTriangle className="mt-0.5 shrink-0" size={16} />;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== recordedAtText) {
    t14 = <div className="flex items-start gap-2 rounded-md bg-status-warning-bg p-3 text-body text-status-warning-text">{t13}<span>You are editing a previously recorded measurement value, recorded on {recordedAtText}.</span></div>;
    $[31] = recordedAtText;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== field.unit || $[34] !== isHeight) {
    t15 = field.unit && !isHeight && <span className="ml-1 text-caption font-normal text-foreground-secondary">({field.unit})</span>;
    $[33] = field.unit;
    $[34] = isHeight;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== field.name || $[37] !== t15) {
    t16 = <Label>{field.name}{t15}</Label>;
    $[36] = field.name;
    $[37] = t15;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== control || $[40] !== errors.heightValue || $[41] !== errors.value || $[42] !== field || $[43] !== isArray || $[44] !== isHeight || $[45] !== isNumeric) {
    t17 = isHeight ? <Controller name="heightValue" control={control} rules={{
      validate: _temp
    }} render={t18 => {
      const {
        field: controllerField
      } = t18;
      return <HeightInput value={controllerField.value} onChange={controllerField.onChange} quarterInches={true} hasError={!!errors.heightValue} errorMessage={errors.heightValue?.message} />;
    }} /> : <MeasurementInput field={field} control={control} name="value" hasError={!!errors.value} errorMessage={errors.value?.message as string | undefined} rules={{
      validate: v_0 => {
        if (isArray) {
          const arr = Array.isArray(v_0) ? v_0 : [];
          if (arr.length === 0) {
            return "Value is required";
          }
          return true;
        }
        const raw = typeof v_0 === "string" ? v_0 : "";
        if (!raw) {
          return "Value is required";
        }
        if (!isNumeric) {
          return true;
        }
        const num = parseFloat(raw);
        if (isNaN(num)) {
          return "Must be a number";
        }
        if (field.minValue != null && num < field.minValue) {
          return `Minimum value is ${field.minValue}`;
        }
        if (field.maxValue != null && num > field.maxValue) {
          return `Maximum value is ${field.maxValue}`;
        }
        return true;
      }
    }} />;
    $[39] = control;
    $[40] = errors.heightValue;
    $[41] = errors.value;
    $[42] = field;
    $[43] = isArray;
    $[44] = isHeight;
    $[45] = isNumeric;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  let t18;
  if ($[47] !== t16 || $[48] !== t17) {
    t18 = <div className="space-y-1">{t16}{t17}</div>;
    $[47] = t16;
    $[48] = t17;
    $[49] = t18;
  } else {
    t18 = $[49];
  }
  let t19;
  if ($[50] !== t14 || $[51] !== t18) {
    t19 = <div className="space-y-4">{t14}{t18}</div>;
    $[50] = t14;
    $[51] = t18;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  let t20;
  if ($[53] !== onSubmit || $[54] !== t19) {
    t20 = <DialogBody><form id="edit-measurement-value-form" onSubmit={onSubmit}>{t19}</form></DialogBody>;
    $[53] = onSubmit;
    $[54] = t19;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  let t21;
  let t22;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <Button variant="ghost" className="text-status-error-text" size="sm" onClick={() => setShowDeleteConfirm(true)}><FiTrash2 size={14} />Delete</Button>;
    t22 = <div className="flex-1" />;
    $[56] = t21;
    $[57] = t22;
  } else {
    t21 = $[56];
    t22 = $[57];
  }
  let t23;
  if ($[58] !== onOpenChange) {
    t23 = <Button variant="secondary" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>;
    $[58] = onOpenChange;
    $[59] = t23;
  } else {
    t23 = $[59];
  }
  let t24;
  if ($[60] !== mutation.isPending) {
    t24 = <Button type="submit" form="edit-measurement-value-form" size="sm" isLoading={mutation.isPending}>Save</Button>;
    $[60] = mutation.isPending;
    $[61] = t24;
  } else {
    t24 = $[61];
  }
  let t25;
  if ($[62] !== t23 || $[63] !== t24) {
    t25 = <DialogFooter>{t21}{t22}{t23}{t24}</DialogFooter>;
    $[62] = t23;
    $[63] = t24;
    $[64] = t25;
  } else {
    t25 = $[64];
  }
  let t26;
  if ($[65] !== t20 || $[66] !== t25) {
    t26 = <DialogContent className="max-w-sm">{t12}{t20}{t25}</DialogContent>;
    $[65] = t20;
    $[66] = t25;
    $[67] = t26;
  } else {
    t26 = $[67];
  }
  let t27;
  if ($[68] !== archiveValueMutation.isPending || $[69] !== deleteConfirmationSubtitle || $[70] !== handleDelete || $[71] !== showDeleteConfirm) {
    t27 = <DeleteConfirmationModal isOpen={showDeleteConfirm} setIsOpen={setShowDeleteConfirm} onConfirm={handleDelete} title="Delete Measurement Value" subtitle={deleteConfirmationSubtitle} confirmText="Delete" isLoading={archiveValueMutation.isPending} />;
    $[68] = archiveValueMutation.isPending;
    $[69] = deleteConfirmationSubtitle;
    $[70] = handleDelete;
    $[71] = showDeleteConfirm;
    $[72] = t27;
  } else {
    t27 = $[72];
  }
  let t28;
  if ($[73] !== onOpenChange || $[74] !== open || $[75] !== t26 || $[76] !== t27) {
    t28 = <Dialog open={open} onOpenChange={onOpenChange}>{t26}{t27}</Dialog>;
    $[73] = onOpenChange;
    $[74] = open;
    $[75] = t26;
    $[76] = t27;
    $[77] = t28;
  } else {
    t28 = $[77];
  }
  return t28;
}
function _temp(v) {
  if (!v.feet && !v.inches) {
    return "Value is required";
  }
  const inches = parseFloat(v.inches);
  if (v.inches && !(inches >= 0 && inches < 12)) {
    return "Inches must be 0\u201311.75";
  }
  return true;
}